import { useEffect, useMemo, useState } from 'react';

export interface DraftElement<U> {
  isDraft: true;
  id: number;
  metadata?: U;
}

export interface PersistentElement<T> {
  isDraft: false;
  data: T;
}

export type ListElement<T, U = T> =
  | DraftElement<U>
  | PersistentElement<T>;

export interface ListOperations<T, U = T> {
  addDraftElement: (options?: {
    before?: ListElement<T, U>;
    metadata?: U;
  }) => void;
  promoteDraftToPersistentElement: (draftId: number, data: T) => void;
  removeElement: (
    itemToRemove: ListElement<T, U> | ((item: ListElement<T, U>) => boolean),
  ) => void;
  updatePersistentElement: (
    dataToUpdate: T | ((data: T) => boolean),
    updatedData: T | ((data: T) => T),
  ) => void;
}

export interface UseDraftListResult<T, U = T> {
  isReady: boolean;
  list: ListElement<T, U>[];
  operations: ListOperations<T, U>;
}

const useDraftList = <T, U = T>(
  currentPersistentItems: T[],
  arePersistentItemsReady?: boolean,
): UseDraftListResult<T, U> => {
  const [listState, setListState] = useState<{
    isReady: boolean;
    list: ListElement<T, U>[];
  }>(() => ({
    isReady: arePersistentItemsReady ?? true,
    list: currentPersistentItems.map(item => ({ isDraft: false, data: item })),
  }));

  const operations = useMemo<ListOperations<T, U>>(() => {
    if (!listState.isReady) {
      return {
        addDraftElement: () => {},
        promoteDraftToPersistentElement: () => {},
        removeElement: () => {},
        updatePersistentElement: () => {},
      };
    }
    return {
      addDraftElement: options => {
        const { before, metadata } = options ?? {};
        setListState(prev => {
          const beforeIndex = before
            ? Math.max(prev.list.indexOf(before), 0)
            : 0;

          const beforeItems = prev.list.slice(0, beforeIndex);
          const afterItems = prev.list.slice(beforeIndex);

          const nextDraftId =
            [...beforeItems].reverse().find(item => item.isDraft)?.id ?? null;
          const previousDraftId =
            afterItems.find(item => item.isDraft)?.id ?? 0;

          const newDraftId =
            previousDraftId +
            (nextDraftId === null ? 1 : (nextDraftId - previousDraftId) / 2);
          return {
            ...prev,
            list: [
              ...beforeItems,
              {
                isDraft: true,
                id: newDraftId,
                metadata
              },
              ...afterItems,
            ],
          };
        });
      },
      promoteDraftToPersistentElement: (draftId: number, data: T) => {
        setListState(prev => ({
          ...prev,
          list: prev.list.map(item =>
            item.isDraft && item.id === draftId
              ? { isDraft: false, data }
              : item,
          ),
        }));
      },
      removeElement: (
        itemToRemove:
          | ListElement<T, U>
          | ((item: ListElement<T, U>) => boolean),
      ) => {
        const hasToRemove =
          typeof itemToRemove === 'function'
            ? itemToRemove
            : (item: ListElement<T, U>) => item === itemToRemove;
        setListState(prev => ({
          ...prev,
          list: prev.list.filter(item => !hasToRemove(item)),
        }));
      },
      updatePersistentElement: (
        dataToUpdate: T | ((data: T) => boolean),
        updatedData: T | ((data: T) => T),
      ) => {
        const toUpdate =
          typeof dataToUpdate === 'function'
            ? (dataToUpdate as (data: T) => boolean)
            : (data: T) => data === dataToUpdate;

        const updateData =
          typeof updatedData === 'function'
            ? (updatedData as (data: T) => T)
            : ((() => updatedData) as (data: T) => T);

        setListState(prev => ({
          ...prev,
          list: prev.list.map(item =>
            !item.isDraft && toUpdate(item.data)
              ? { ...item, data: updateData(item.data) }
              : item,
          ),
        }));
      },
    };
  }, [listState.isReady]);

  useEffect(() => {
    if (!listState.isReady && arePersistentItemsReady) {
      setListState({
        isReady: true,
        list: currentPersistentItems.map(data => ({ isDraft: false, data })),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arePersistentItemsReady]);

  return { isReady: listState.isReady, list: listState.list, operations };
};

export default useDraftList;
